<script setup>
import { computed, ref, watch } from 'vue'
import { useUiStore } from '@/stores/UiStore'
import { useLoadsStore } from '@/stores/Loads'
import { useToastStore } from '@/stores/Toast'
import { UPDATE_TRUCK_LOCATION } from '@/constants/dispatch'

/**
 * The default (Kansas City, MO) geo coordinates to be used by the map.
 *
 * @type {Object}
 */
const DEFAULT_GEO_LOCATION = {
    lat: 39.0997265,
    lng: -94.5785667,
}

/**
 * The store instance.
 */
const uiStore = useUiStore()
const store = useLoadsStore()
const toastStore = useToastStore()

/**
 * The local refs.
 */
const markers = ref([])
const center = ref(null)
const address = ref(null)
const isLoading = ref(false)
const description = ref(null)
const autoDescription = ref(true)

/**
 * The load data from the store.
 *
 * @return {Object}
 */
const data = computed(() => {
    return uiStore.actionLoads(UPDATE_TRUCK_LOCATION)
})

/**
 * The origination terminal geo coordinates.
 *
 * @return {Object}
 */
const originationTerminalGeo = computed(() => {
    return data.value?.origination_terminal?.geo
})

/**
 * The current location of the truck.
 *
 * @return {Object}
 */
const currentLocation = computed(() => {
    const currentLocation = data.value?.current_location?.geo

    const geo = currentLocation || originationTerminalGeo.value

    if (!geo) {
        return DEFAULT_GEO_LOCATION
    }

    return {
        lat: geo.latitude,
        lng: geo.longitude,
    }
})

/**
 * Determine if the form has errors.
 *
 * @return {Boolean}
 */
const hasErrors = computed(() => {
    return (
        markers.value.length === 0 ||
        (!autoDescription.value && !description.value)
    )
})

/**
 * Determine if the action should be shown.
 *
 * @return {Boolean}
 */
const show = computed(() => {
    return uiStore.isActionActive(UPDATE_TRUCK_LOCATION)
})

/**
 * Handle the accept click event.
 *
 * @return {void}
 */
const onAccept = async () => {
    try {
        isLoading.value = true

        const payload = {
            description: autoDescription.value
                ? address.value
                : description.value,
            geo: {
                latitude: markers.value[0].position.lat,
                longitude: markers.value[0].position.lng,
            },
            vehicle_id: data.value.truck?.id,
        }

        await store.setTruckLocation({
            loadId: data.value?.id,
            payload,
        })

        toastStore.setNotification({
            message: `Truck's location updated successfully`,
            type: 'success',
        })
    } catch (error) {
        if (error && typeof error?.message === 'function') {
            toastStore.setNotification({
                message: error.message(),
                type: 'error',
            })
        }

        console.error(error)
    } finally {
        isLoading.value = false
        onCancel()
    }
}

/**
 * Handle the place change event.
 *
 * @param {Object} place
 * @return {void}
 */
const onPlaceChange = (place) => {
    center.value = {
        lat: place.geometry.location.lat(),
        lng: place.geometry.location.lng(),
    }

    address.value = place.formatted_address.replace(/, USA$/, '')

    markers.value = [
        {
            position: {
                lat: place.geometry.location.lat(),
                lng: place.geometry.location.lng(),
            },
        },
    ]
}

/**
 * Handle the cancel click event.
 *
 * @return {void}
 */
const onCancel = () => {
    uiStore.closeAction(UPDATE_TRUCK_LOCATION)
}

/**
 * Watch for the show value.
 */
watch(
    show,
    (value) => {
        if (value) {
            center.value = {
                ...currentLocation.value,
            }
        }
    },
    { immediate: true }
)
</script>

<template>
    <v-dialog
        data-test="confirmation-dialog"
        v-model="show"
        persistent
        width="750"
    >
        <v-card>
            <v-card-item
                title="Set Truck Location"
                density="compact"
                class="pb-2 bg-gray-50 border-b"
            >
                <template v-slot:append>
                    <v-icon
                        icon="fas fa-close"
                        size="x-small"
                        @click="onCancel"
                    >
                    </v-icon>
                </template>
            </v-card-item>
            <v-card-text data-test="confirmation-dialog-body">
                <div class="flex justify-between">
                    <div class="w-4/5 mr-3">
                        <GMapAutocomplete
                            class="w-full p-4 bg-[#ededed] mb-3 !border-b border-gray-700 placeholder-[#717171] rounded shadow-md"
                            placeholder="Location"
                            @place_changed="onPlaceChange"
                        />
                        <v-textarea
                            v-if="!autoDescription"
                            autofocus
                            no-resize
                            rows="2"
                            v-model="description"
                            variant="solo-filled"
                            label="Description"
                        ></v-textarea>
                        <v-switch
                            color="blue"
                            label="Generate Description"
                            tooltip="test"
                            v-model="autoDescription"
                        ></v-switch>
                    </div>
                    <GMapMap :center="center" :zoom="7" class="w-full h-72">
                        <GMapCircle
                            :key="index"
                            v-for="(marker, index) in markers"
                            :radius="400"
                            :center="marker.position"
                        />
                    </GMapMap>
                </div>
            </v-card-text>
            <div class="bg-gray-100 border-t py-2 px-4">
                <div class="text-xs">
                    If <strong>Generate Description</strong> is enabled; the
                    address field will be used as the display value of the
                    location.
                </div>
            </div>
            <v-spacer class="border-b"></v-spacer>
            <v-card-actions class="flex px-4 justify-end">
                <v-btn
                    data-test="confirmation-dialog-btn-close"
                    variant="outlined"
                    @click="onCancel"
                >
                    Cancel
                </v-btn>
                <v-btn
                    :loading="isLoading"
                    :disabled="hasErrors"
                    color="primary"
                    variant="flat"
                    data-test="confirmation-dialog-btn-accept"
                    @click="onAccept"
                >
                    Save
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>
<style>
.pac-container {
    z-index: 9999;
}
</style>
